import { TileLayer, DomEvent, Util, DomUtil } from "leaflet";
import PouchDB from "pouchdb";
import _ from "lodash";

// HTMLCanvasElement.toBlob() polyfill
// copy-pasted off https://developer.mozilla.org/en-US/docs/Web/API/HTMLCanvasElement/toBlob

if (!HTMLCanvasElement.prototype.toBlob) {
  Object.defineProperty(HTMLCanvasElement.prototype, "toBlob", {
    value: function(callback, type, quality) {
      const dataURL = this.toDataURL(type, quality).split(",")[1];
      setTimeout(() => {
        const binStr = atob(dataURL),
          len = binStr.length,
          arr = new Uint8Array(len);

        for (let i = 0; i < len; i++) {
          arr[i] = binStr.charCodeAt(i);
        }

        callback(new Blob([arr], { type: type || "image/png" }));
      });
    }
  });
}

const cacheLogEnabled = false;

class CachedTileLayer extends TileLayer {
  constructor(url, options) {
    // noinspection JSCheckFunctionSignatures
    super(url, options);
    this.setOptions(options);
    this._db = null;
  }

  initialize(url, options) {
    options = this.setOptions(options);
    TileLayer.prototype.initialize.call(this, url, options);
  }

  setOptions(options) {
    const temp = _.defaults(options, {
      useCache: false,
      saveToCache: true,
      useOnlyCache: false,
      cacheFormat: "image/png",
      cacheMaxAge: 24 * 3600 * 1000
    });
    Util.setOptions(this, temp);

    return this.options;
  }

  createTile(coords, done) {
    const tile = DomUtil.create("img");

    DomEvent.on(tile, "error", Util.bind(this._tileOnError, this, done, tile));

    if (this.options.crossOrigin || this.options.crossOrigin === "") {
      tile.crossOrigin =
        this.options.crossOrigin === true ? "" : this.options.crossOrigin;
    }

    tile.alt = "";
    tile.setAttribute("role", "presentation");
    const tileUrl = this.getTileUrl(coords);

    if (this.options.useCache && this._db) {
      this._db.get(
        tileUrl,
        { revs_info: true },
        this._onCacheLookup(tile, tileUrl, done)
      );
    } else {
      // fall back to standard behaviour
      DomEvent.on(tile, "load", Util.bind(this._tileOnLoad, this, done, tile));
      tile.src = tileUrl;
    }

    return tile;
  }

  _onCacheLookup(tile, tileUrl, done) {
    return (err, data) => {
      if (data) {
        this._onCacheHit(tile, tileUrl, data, done);
      } else {
        this._onCacheMiss(tile, tileUrl, done);
      }
    };
  }

  _onCacheHit(tile, tileUrl, data, done) {
    this.fire("tilecachehit", {
      tile: tile,
      url: tileUrl
    });

    // read the attachment as blob
    this._db.getAttachment(tileUrl, "tile").then(blob => {
      const url = URL.createObjectURL(blob);
      if (
        Date.now() > data.timestamp + this.options.cacheMaxAge &&
        !this.options.useOnlyCache
      ) {
        // tile is too old, try to refresh it
        if (cacheLogEnabled) {
          console.log("Tile is too old: ", tileUrl);
        }

        if (this.options.saveToCache) {
          DomEvent.on(
            tile,
            "load",
            Util.bind(
              this._saveTile,
              this,
              tile,
              tileUrl,
              data._revs_info[0].rev,
              done
            )
          );
        }
        tile.crossOrigin = "Anonymous";
        tile.dataset.cached = "0";
        tile.src = tileUrl;
        DomEvent.on(tile, "error", function(e) {
          this.src = url;
          done(e, tile);
        });
      } else {
        // serve tile from cached data
        DomEvent.on(
          tile,
          "load",
          Util.bind(this._tileOnLoad, this, done, tile)
        );
        tile.dataset.cached = "1";
        tile.src = url;
      }
    });
  }

  _onCacheMiss(tile, tileUrl, done) {
    this.fire("tilecachemiss", {
      tile: tile,
      url: tileUrl
    });

    tile.dataset.cached = "0";

    if (this.options.useOnlyCache) {
      // offline, not cached
      tile.onload = Util.falseFn;
      tile.src = Util.emptyImageUrl;
    } else {
      // online, not cached, request the tile normally
      if (this.options.saveToCache) {
        DomEvent.on(
          tile,
          "load",
          Util.bind(this._saveTile, this, tile, tileUrl, undefined, done)
        );
      } else {
        DomEvent.on(
          tile,
          "load",
          Util.bind(this._tileOnLoad, this, done, tile)
        );
      }
      tile.crossOrigin = "Anonymous";
      tile.src = tileUrl;
    }
  }

  _saveTile(tile, tileUrl, existingRevision, done) {
    if (!this.options.saveToCache) {
      return;
    }

    const canvas = DomUtil.create("canvas");
    canvas.width = tile.naturalWidth || tile.width;
    canvas.height = tile.naturalHeight || tile.height;

    const context = canvas.getContext("2d");
    context.drawImage(tile, 0, 0);

    const format = this.options.cacheFormat;

    canvas.toBlob(blob => {
      setTimeout(() => {
        this._db
          .put({ _id: tileUrl, _rev: existingRevision, timestamp: Date.now() })
          .then(status =>
            this._db.putAttachment(tileUrl, "tile", status.rev, blob, format)
          )
          .then(resp => done && done())
          // saving the tile to the cache might have failed,
          // but the tile itself has been loaded.
          .catch(() => done && done());
      }, 1);
    }, format);
  }

  _tileOnLoad(done, tile) {
    // this won't support IE < 9
    // https://github.com/Leaflet/Leaflet/issues/3332
    done(null, tile);
  }

  _tileOnError(done, tile, e) {
    const errorUrl = this.options.errorTileUrl;
    if (errorUrl && tile.getAttribute("src") !== errorUrl) {
      tile.src = errorUrl;
    }
    done(e, tile);
  }
}

TileLayer.addInitHook(function() {
  if (!this.options.useCache) {
    this._db = null;
    return;
  }

  this._db = new PouchDB("offline-tiles");
});

export { CachedTileLayer };
